import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { Barra, BotaoExportar, Kpi, LinhaEscopo, NomeParceiro, percentualTexto, Rolagem, Segmentado, TD, TH, useParametro, usePeriodo, Valor } from './comum';
import { SeletorIntercompany, useIntercompany } from './Dre';
import { BASES, type Base, type Parceiros as ParceirosApi } from './tipos';

const COR_CLASSE = { A: 'verde', B: 'laranja', C: 'neutro' } as const;

/** R6 · Analítico por cliente e fornecedor: participação que soma 100,00% e curva ABC. */
export function Parceiros() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const [base, setBase] = useParametro<Base>('base', 'competencia', BASES);
  const [lado, setLado] = useParametro<'clientes' | 'fornecedores'>('lado', 'clientes', ['clientes', 'fornecedores']);
  const [inter, setInter, interVigente] = useIntercompany();
  const params = { de: periodo.de, ate: periodo.ate, base, lado, intercompany: inter };
  const q = useQuery({ queryKey: ['/relatorios/parceiros', params], queryFn: () => get<ParceirosApi>('/relatorios/parceiros', params), placeholderData: keepPreviousData });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <Segmentado
          rotulo={t('parceiros.lado')}
          valor={lado}
          aoMudar={setLado}
          opcoes={[
            { valor: 'clientes', rotulo: t('parceiros.clientes') },
            { valor: 'fornecedores', rotulo: t('parceiros.fornecedores') },
          ]}
        />
        <Segmentado
          rotulo={t('comum.baseRotulo')}
          valor={base}
          aoMudar={setBase}
          opcoes={[
            { valor: 'competencia', rotulo: t('comum.base.competencia') },
            { valor: 'caixa', rotulo: t('comum.base.caixa') },
          ]}
        />
        {q.data?.escopo.modo === 'consolidado' && <SeletorIntercompany valor={interVigente} aoMudar={setInter} />}
        <BotaoExportar relatorio="parceiros" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <LinhaEscopo escopo={d.escopo} />
            {d.por_moeda.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('parceiros.vazioTitulo')} texto={t('parceiros.vazioTexto')} />
              </Cartao>
            ) : (
              d.por_moeda.map((m) => (
                <section key={m.moeda} aria-label={t('comum.moeda', { moeda: m.moeda })} className="flex min-w-0 flex-col gap-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Kpi rotulo={`${t('comum.total')} (${m.moeda})`} valor={dinheiro(m.total_centavos, m.moeda)} />
                    {m.abc.map((c) => (
                      <Kpi key={c.classe} rotulo={t('parceiros.classeResumo', { classe: c.classe, count: c.parceiros })} valor={dinheiro(c.valor_centavos, m.moeda)} />
                    ))}
                  </div>
                  <Cartao titulo={t('parceiros.curva')} semPadding>
                    <Rolagem rotulo={`${t('comum.tabela')} (${m.moeda})`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-borda text-left text-tinta-3">
                            <th scope="col" className={TH}>{t('parceiros.parceiro')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('parceiros.valor')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('parceiros.participacao')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('parceiros.acumulado')}</th>
                            <th scope="col" className={`${TH} text-center`}>{t('parceiros.classe')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.parceiros.map((p) => (
                            <tr key={p.parceiro_id ?? 'sem'} className="border-b border-borda last:border-0">
                              <th scope="row" className={`${TD} max-w-64 text-left font-normal break-words`}>
                                {p.parceiro_id ? <NomeParceiro nome={p.nome} vazio={t('comum.semNome')} /> : t('comum.semParceiro')}
                              </th>
                              <td className={`${TD} text-right`}>
                                <Valor centavos={p.valor_centavos} moeda={m.moeda} />
                              </td>
                              <td className={`${TD} numero text-right`}>{percentualTexto(p.participacao)}</td>
                              <td className={`${TD} numero text-right text-tinta-2`}>{percentualTexto(p.acumulado)}</td>
                              <td className={`${TD} text-center`}>{p.classe && <Selo cor={COR_CLASSE[p.classe]}>{p.classe}</Selo>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Rolagem>
                    <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('parceiros.ajuda')}</p>
                  </Cartao>
                </section>
              ))
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
